import React, { useEffect, useState } from 'react';
import { useFormikContext } from 'formik';
import {
  lookupsJson,
  mineSubsidenceOptional,
  waterBackupNameByState,
  equipmentBreakdownServiceLineCoveragesStates,
  EBSLstateDate,
  arFortifiedRoofEndorsementEffectiveDate
} from '@ourbranch/lookups';
import { isAfter, isSameDay } from 'date-fns';
import Grid from '@material-ui/core/Grid';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import { getShowMSALE } from 'offer/helpers/get-coverage';
import { lockedValuesTooltipTexts, tooltipHoverTexts } from 'core/helpers/constants';
import { useStore } from 'core/store';
import { useToast } from 'core/components/toast';
import Field from 'core/components/form/form.v2';
import { Label } from 'core/components/label';
import { LabelTooltip } from 'core/components/label-tooltip';
import { awsDateToDateObject, numberFormatter } from 'core/helpers/formatters';
import { localToUtcTime } from 'customer/components/policy/dates';
import useOptions from 'core/hooks/use-options';
import useStyles from '../coverages.styles';
import useExtendedRoofCoverage from '../../hooks/roof-logic/use-extended-roof-coverage';
import RoofSurfacesCoverage from './roof-surface-coverage';
import RoofSurfaceCoverageModal from './roof-surface-coverage/roof-surface-coverage-modal';
import icViewMore from 'common/assets/icViewMore_12px.svg';

function OtherCoverages({ id, enforceMS, state, coverageA, fromPolicy }) {
  const classes = useStyles();
  const {
    values: { homeCoverage, home, ...values },
    initialValues,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const toast = useToast();

  const optionalMS =
    home.county &&
    mineSubsidenceOptional[state] &&
    (mineSubsidenceOptional[state][home.county.toUpperCase()] || mineSubsidenceOptional[state].all);

  const showMSALE = getShowMSALE(state);

  const waterBackupLabel = (waterBackupNameByState && waterBackupNameByState[state]) || 'Water Backup';

  const { account, offer: offerStore } = useStore();
  const policyStore = account?.policies?.policy;
  const offerWaterBackup =
    policyStore?.policy?.offer?.quote?.offerings?.offerWaterBackup ||
    offerStore?.offer?.quote?.offerings?.offerWaterBackup ||
    offerStore?.offer?.offerings?.find((o) => o.name === 'WaterBackup');

  let canAddEquipmentBreakdownServiceLineCoverages = equipmentBreakdownServiceLineCoveragesStates.includes(state);
  const rateControlDate = awsDateToDateObject(offerStore?.offer?.quote?.global.rateControlDate);
  const StateDate = new Date(EBSLstateDate[state]);

  const medLimitOptions = useOptions('guestMedicalLimit', state, values?.[id]?.coverageY);
  const coverageBCPctOfAOptions = useOptions('buildingCodeCoverageOptions', state);
  const coverageGOptions = useOptions('coverageG', state, values?.[id]?.coverageG);
  const waterBackupOptions = useOptions('waterBackupCoverageOptions', state, values?.[id]?.coverageWaterBackup);

  if (fromPolicy) {
    const { updatedDateTime } = policyStore?.policy?.versionHistory?.find(({ version }) => version === 1) || {};
    let createdDate = localToUtcTime(updatedDateTime, state);
    if (state === 'TX') {
      // exception in TX to look at effectiveDate because of early publishing
      createdDate = localToUtcTime(policyStore?.policy.effectiveDate, state);
    }
    canAddEquipmentBreakdownServiceLineCoverages =
      canAddEquipmentBreakdownServiceLineCoverages &&
      policyStore?.policy?.isBix &&
      (isAfter(createdDate, StateDate) || isSameDay(StateDate, createdDate));
  } else {
    // new offer
    canAddEquipmentBreakdownServiceLineCoverages =
      canAddEquipmentBreakdownServiceLineCoverages &&
      offerStore?.offer?.quote?.isBix &&
      (isAfter(rateControlDate, StateDate) || isSameDay(StateDate, rateControlDate));
  }

  // AR Fortified Roof is date-gated to effective date >= arFortifiedRoofEndorsementEffectiveDate
  // (7/1/2026) to match the rater (cd-home-rater.ts fortified_roof_endtYN) and the policy
  // document generation, so the field isn't selectable on pre-gate AR policies (which the
  // rater would not price). KY is not date-gated. Mirrors the EBSL date-gate pattern above.
  // See #30008.
  const arFrofEffectiveDate = new Date(arFortifiedRoofEndorsementEffectiveDate);
  let canShowFortifiedRoof = state === 'KY';
  if (state === 'AR') {
    const frofEffectiveDate = fromPolicy ? localToUtcTime(policyStore?.policy?.effectiveDate, state) : rateControlDate;
    canShowFortifiedRoof =
      !!frofEffectiveDate &&
      (isAfter(frofEffectiveDate, arFrofEffectiveDate) || isSameDay(frofEffectiveDate, arFrofEffectiveDate));
  }

  // Roof related Logic
  const { forcedScheduledRSCoverage } = useExtendedRoofCoverage(home, state, fromPolicy, initialValues, homeCoverage);
  const lockedFRField = !homeCoverage.coverageRSExtended || forcedScheduledRSCoverage || home.fortifiedHome > 0;

  const [openRoofSurfaceModal, setOpenRoofSurfaceModal] = useState(false);
  const learnMoreRoofSurfaceTooltip = {
    label: 'Learn more',
    onHoverText: 'Click to learn more',
    clickIcon: icViewMore,
    onClick: () => setOpenRoofSurfaceModal(true)
  };

  useEffect(() => {
    if (!['KY', 'AR'].includes(state)) {
      return;
    } //inside {} for biome/linter rules
    if (home.fortifiedHome > 0 && homeCoverage.coverageFortifiedRoof) {
      setFieldValue(`${id}.coverageFortifiedRoof`, false);
      setFieldTouched(`${id}.coverageFortifiedRoof`, true, false);
      toast.notify({
        type: 'success',
        message:
          'Your certification level qualifies for the Fortified Home Discount. The Fortified Roof Endorsement has been removed since it is no longer needed.'
      });
    }
  }, [home.fortifiedHome, homeCoverage.coverageFortifiedRoof, id, state, setFieldValue, setFieldTouched, toast]);

  useEffect(() => {
    if (!['KY', 'AR'].includes(state)) {
      return;
    } //inside {} for biome/linter rules
    if (homeCoverage.coverageFortifiedRoof && !homeCoverage.coverageRSExtended) {
      setFieldValue(`${id}.coverageFortifiedRoof`, false);
      setFieldTouched(`${id}.coverageFortifiedRoof`, true, false);
      toast.notify({
        type: 'success',
        message:
          'Based on the roof year and Roof Surfaces coverage selected, the Fortified Roof Upgrade Endorsement is no longer available.'
      });
    }
  }, [
    homeCoverage.coverageRSExtended,
    homeCoverage.coverageFortifiedRoof,
    forcedScheduledRSCoverage,
    id,
    state,
    setFieldValue,
    setFieldTouched,
    toast
  ]);

  return (
    <Grid container item spacing={5} className={classes.coverageContainer} alignItems="flex-start">
      <Grid item xs={12}>
        <Label key="lblTop" type="formSubTitle" className={classes.cardSubSectionDivider}>
          Other Coverages
        </Label>
      </Grid>
      <Grid item xs={6}>
        <LabelTooltip
          label="Family Liability"
          tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.familyLiability }}
        >
          <Field
            id={`${id}.coverageX`}
            name={`${id}.coverageX`}
            type="select"
            mode="dark"
            options={useOptions('coverageX', state, values?.[id]?.coverageX)}
          />
        </LabelTooltip>
      </Grid>
      <Grid item xs={6}>
        <LabelTooltip
          label="Guest Medical Limit"
          tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.guestMedicalLimit }}
        >
          <Field id={`${id}.coverageY`} name={`${id}.coverageY`} type="select" mode="dark" options={medLimitOptions} />
        </LabelTooltip>
      </Grid>
      <Grid item xs={6}>
        <LabelTooltip
          label="Loss Assessments"
          tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.lossAssessments }}
        >
          <Field id={`${id}.coverageG`} name={`${id}.coverageG`} type="select" mode="dark" options={coverageGOptions} />
        </LabelTooltip>
      </Grid>
      <Grid item xs={6}>
        <LabelTooltip
          label="Building Code Coverage"
          tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.buildingCodeCoverage }}
        >
          <Field
            id={`${id}.coverageBCPctOfA`}
            name={`${id}.coverageBCPctOfA`}
            type="select"
            mode="dark"
            fast={false}
            options={coverageBCPctOfAOptions.map((option) => ({
              id: option.id,
              value: `${option.value} ($${numberFormatter((option.id / 100) * coverageA)})`
            }))}
          />
        </LabelTooltip>
      </Grid>
      <Grid item xs={6}>
        <LabelTooltip
          label={waterBackupLabel}
          tooltip={{ label: 'More Info', onHoverText: tooltipHoverTexts.waterBackup }}
        >
          <Field
            id={`${id}.coverageWaterBackup`}
            name={`${id}.coverageWaterBackup`}
            type="select"
            mode="dark"
            className="has-custom-error"
            disabled={!offerWaterBackup}
            options={waterBackupOptions}
          />
          {!offerWaterBackup && (
            <Label type="formLabel" className={classes.errorMessage}>
              Cannot offer water backup due to history of water backup claims
            </Label>
          )}
        </LabelTooltip>
      </Grid>

      <RoofSurfacesCoverage
        state={state}
        id={id}
        fromPolicy={fromPolicy}
        learnMoreRoofSurfaceTooltip={learnMoreRoofSurfaceTooltip}
      />

      {canAddEquipmentBreakdownServiceLineCoverages && (
        <Grid item xs={6}>
          <Field
            id={`${id}.coverageEBSL`}
            name={`${id}.coverageEBSL`}
            type="checkbox"
            label="Equipment Breakdown & Service Line"
            mode="dark"
            tooltipText={tooltipHoverTexts.coverageEBSL}
            tooltipLabel="More Info"
          />
        </Grid>
      )}

      {canShowFortifiedRoof && (
        <Grid item xs={6}>
          {lockedFRField ? (
            <Field
              id={`${id}.coverageFortifiedRoof`}
              name={`${id}.coverageFortifiedRoof`}
              label="Fortified Roof Upgrade Endorsement"
              type="value"
              mode="locked"
              value={'This coverage is not available'}
              lockedValue={{ text: lockedValuesTooltipTexts.lockedFortifiedRoofEndorsement }}
              showTooltip={learnMoreRoofSurfaceTooltip}
              className={classes.lockedRoofSurface}
            />
          ) : (
            <Field
              id={`${id}.coverageFortifiedRoof`}
              name={`${id}.coverageFortifiedRoof`}
              type="checkbox"
              label="Fortified Roof Upgrade Endorsement"
              mode="dark"
              tooltipText={tooltipHoverTexts.fortifiedRoofEndorsement}
              tooltipLabel="More Info"
            />
          )}
        </Grid>
      )}

      {(optionalMS || enforceMS) && (
        <>
          {showMSALE && (
            <Field
              id={`${id}.coverageMSALE`}
              name={`${id}.coverageMSALE`}
              type="select"
              label="Mine Subsidence Additional Expense Coverage"
              mode="dark"
              xs={6}
              disabled={!homeCoverage.coverageMS}
              options={lookupsJson.mineSubsidenceALECoverageOptions}
            />
          )}
          <Field
            id={`${id}.coverageMS`}
            name={`${id}.coverageMS`}
            type="checkbox"
            label="Mine Subsidence"
            mode="dark"
            xs={6}
            disabled={!!enforceMS}
          />
        </>
      )}

      <RoofSurfaceCoverageModal open={openRoofSurfaceModal} onClose={() => setOpenRoofSurfaceModal(false)} />
    </Grid>
  );
}

OtherCoverages.propTypes = {
  id: PropTypes.string.isRequired,
  enforceMS: PropTypes.bool.isRequired,
  state: PropTypes.string.isRequired,
  coverageA: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  fromPolicy: PropTypes.bool.isRequired
};

export default observer(OtherCoverages);
